import React, { useEffect, useState } from 'react';
import { Loader2, Send, X } from 'lucide-react';
import ModalPortal from '../../components/ModalPortal';

const RepairBulkNoteModal = ({ open, devices, api, onClose, onSent }) => {
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const refs = (Array.isArray(devices) ? devices : [])
    .map((d) => d?.ref)
    .filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    setText('');
    setVisibility('public');
    setSending(false);
    setError('');
    return undefined;
  }, [open]);

  const handleSend = async () => {
    const message = text.trim();
    if (!message) {
      setError('Saisissez le message à envoyer.');
      return;
    }
    if (!refs.length) {
      setError('Aucun dossier sélectionné.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await api.post('/repair-notes/bulk', {
        device_refs: refs,
        text: message,
        visibility,
      });
      const sent = Number(res.data?.sent || 0);
      const skipped = Array.isArray(res.data?.skipped) ? res.data.skipped : [];
      onSent?.({ sent, skipped, visibility });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Envoi impossible');
      setSending(false);
    }
  };

  return (
    <ModalPortal isOpen={open}>
      <div className="modal-dark w-full max-w-lg" data-testid="repair-bulk-note-modal">
        <div className="modal-dark-header flex items-center justify-between">
          <div>
            <h3 className="font-bold text-white">Envoyer une note</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {refs.length} réparation{refs.length > 1 ? 's' : ''} sélectionnée{refs.length > 1 ? 's' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="max-h-24 overflow-y-auto rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs text-gray-300 flex flex-wrap gap-1.5">
            {refs.map((ref) => (
              <span key={ref} className="px-2 py-0.5 rounded bg-white/10 font-semibold text-red-300">
                {ref}
              </span>
            ))}
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2.5 text-sm text-white min-h-[120px]"
            placeholder="Ex. : La pièce est en retard chez le fournisseur, nous vous tiendrons informé dès réception."
            data-testid="repair-bulk-note-text"
          />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVisibility('public')}
              className={`flex-1 min-h-[40px] rounded-lg text-xs font-bold border ${
                visibility === 'public'
                  ? 'bg-emerald-900/40 border-emerald-500/40 text-emerald-200'
                  : 'border-white/10 text-gray-400 hover:bg-white/10'
              }`}
              data-testid="repair-bulk-note-public"
            >
              Public — clients informés
            </button>
            <button
              type="button"
              onClick={() => setVisibility('internal')}
              className={`flex-1 min-h-[40px] rounded-lg text-xs font-bold border ${
                visibility === 'internal'
                  ? 'bg-amber-900/40 border-amber-500/40 text-amber-200'
                  : 'border-white/10 text-gray-400 hover:bg-white/10'
              }`}
            >
              Interne — atelier seulement
            </button>
          </div>

          {visibility === 'public' && (
            <p className="text-[11px] text-gray-500">
              Chaque client voit la note dans son espace et recoit un email s'il a une adresse.
            </p>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] px-4 rounded-lg text-sm text-gray-300 hover:bg-white/10"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !refs.length}
              className="min-h-[44px] px-4 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="repair-bulk-note-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default RepairBulkNoteModal;
